<script lang="ts">
  import {
    dndzone,
    SOURCES,
    overrideItemIdKeyNameBeforeInitialisingDndZones
  } from 'svelte-dnd-action';
  import {flip} from 'svelte/animate';
  import {fade, slide} from 'svelte/transition';
  import InlineSVG from 'svelte-inline-svg';

  import type {ImageFile} from '../../types/project';

  import dataUrlToBlob from '../../utils/data-url-to-blob';

  import infoIcon from '../../assets/icons/info.svg';
  import sortIcon from '../../assets/icons/select-arrows.svg';

  import Note from '../note/Note.svelte';
  import ImageCaptionInput from './ImageCaptionInput.svelte';

  export let files: Array<ImageFile> = [];
  export let images: Array<any> = [];
  export let captions: Record<string, string> = {};

  // Updates the image preview and file data after cropping
  async function handleFileCrop(event: CustomEvent) {
    const {name, dataUrl, width, height} = event.detail;
    const blob = await dataUrlToBlob(dataUrl);
    files = files.map(file =>
      file.name === name ? {...file, url: dataUrl, blob, width, height} : file
    );
  }

  // Updates the image preview and data after setting focus point
  function handleImageFocus(event: CustomEvent) {
    const {name, focusPoint} = event.detail;
    images = images.map(image =>
      image.name === name ? {...image, focusPoint} : image
    );
  }

  // Drag and Drop

  // Make svelte-dnd-action use the property 'name' instead of 'id'
  overrideItemIdKeyNameBeforeInitialisingDndZones('name');

  let dragDisabled = true;
  let showCoverImageHint = true;
  const flipDurationMs = 200;
  const dropTargetStyle = {};

  function handleConsider(event: CustomEvent) {
    showCoverImageHint = false;
    images = event.detail.items;
  }

  function handleFinalize(event: CustomEvent) {
    images = event.detail.items;
    showCoverImageHint = true;
    if (event.detail.info.source === SOURCES.POINTER) {
      dragDisabled = true;
    }
  }

  function startDrag(event: MouseEvent | TouchEvent) {
    event.preventDefault();
    dragDisabled = false;
    showCoverImageHint = false;
  }
</script>

<style lang="postcss">
  .container {
    display: grid;
    gap: 0.5rem;
    margin: -1rem 0 1.5rem;
  }

  @media (min-width: 768px) {
    .container {
      grid-template-columns: 1fr 2fr 1fr;
      align-items: start;
      gap: 1rem 1.5rem;
      margin-top: -0.5rem;
    }
  }

  .newImages,
  .projectImages {
    display: grid;
    grid-auto-flow: row;
    gap: 0.75rem;
  }

  .newImage,
  .projectImage {
    display: grid;
    gap: 0.25rem;
  }

  .newImage {
    grid-template-columns: 1fr auto;
  }

  .projectImage {
    grid-template-columns: auto 1fr auto;
  }

  @media (min-width: 640px) {
    .projectImage {
      grid-template-columns: 6.2rem 1fr auto;
    }
  }

  @media (min-width: 768px) {
    .newImages {
      grid-column-start: 2;
      grid-column-end: 4;
      gap: 1rem;
    }

    .newImage {
      grid-template-columns: 2fr 1fr;
      gap: 1.5rem;
    }

    .projectImages {
      grid-column-start: 1;
      grid-column-end: 4;
      gap: 1rem;
    }

    .projectImage {
      grid-template-columns: 1fr 2fr 1fr;
      gap: 1.5rem;
    }
  }

  .dragWrapper {
    display: flex;
    align-items: center;
    justify-content: end;
    gap: 0.25rem;
    color: var(--ubilabs-gray-dark-2);
  }

  .dragWrapper :global(svg) {
    display: flex;
  }

  @media (max-width: 639px) {
    .dragWrapper {
      max-width: 3rem;
      font-size: 0.75rem;
    }
  }

  @media (min-width: 768px) {
    .imageCaptionsInputNote {
      grid-column: 2;
    }
  }
</style>

<div class="container">
  <!-- New project images / upload preview -->
  {#if files?.length}
    <div class="newImages">
      {#each files as file}
        <div class="newImage">
          <ImageCaptionInput
            bind:image={file}
            bind:caption={captions[file.name]}
            cropable
            on:crop={handleFileCrop}
            deletable={Boolean(file.onDelete)}
            on:delete={() => {
              if (file.onDelete) {
                file.onDelete();
              }
            }} />
        </div>
      {/each}
    </div>

    <div class="imageCaptionsInputNote" in:slide|local>
      <Note variant="info">
        <span slot="icon"><InlineSVG src={infoIcon} /></span>
        To edit the order of the newly added images, please save the project first.
      </Note>
    </div>
  {/if}

  <!-- Project images -->
  {#if images?.length}
    <div
      use:dndzone={{
        items: images,
        dragDisabled,
        flipDurationMs,
        dropTargetStyle
      }}
      on:consider={handleConsider}
      on:finalize={handleFinalize}
      class="projectImages">
      {#each images as image, imageIndex (image.name)}
        <div class="projectImage" animate:flip={{duration: flipDurationMs}}>
          <div class="dragWrapper">
            {#if imageIndex === 0 && showCoverImageHint}
              <span transition:fade|local>Cover Image</span>
            {/if}
            <span
              style={dragDisabled ? 'cursor: grab' : 'cursor: grabbing'}
              on:mousedown={startDrag}
              on:touchstart={startDrag}>
              <InlineSVG src={sortIcon} />
            </span>
          </div>

          <ImageCaptionInput
            bind:image
            bind:caption={captions[image.name]}
            on:changecaption={event => {
              const {name, caption} = event.detail;
              images = images.map(data =>
                data.name === name ? {...data, caption} : data
              );
            }}
            deletable
            on:delete={event => {
              images = images.filter(
                filterImage => filterImage.name !== event.detail.name
              );
            }}
            focusEditable={imageIndex === 0 &&
              image.width / image.height !== 16 / 9}
            on:setfocus={handleImageFocus} />
        </div>
      {/each}
    </div>
  {/if}
</div>
